import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  TextField,
  InputAdornment,
  makeStyles,
  IconButton,
  Typography,
  Divider,
  CircularProgress,
  Box,
  Tooltip,
  Button,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import CloseIcon from "@material-ui/icons/Close";
import FlashOnIcon from "@material-ui/icons/FlashOn";
import VisibilityIcon from "@material-ui/icons/Visibility";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { getQuickMessageMedias } from "../../utils/quickMessageMedias";

const useStyles = makeStyles((theme) => ({
  dialog: {
    "& .MuiDialog-paper": {
      width: "500px",
      maxHeight: "600px",
    },
  },
  previewDialog: {
    "& .MuiDialog-paper": {
      width: "600px",
      maxHeight: "80vh",
    },
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingRight: theme.spacing(1),
  },
  searchField: {
    margin: theme.spacing(2, 0),
  },
  listItem: {
    cursor: "pointer",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
    borderRadius: "8px",
    margin: theme.spacing(0.5, 0),
    paddingRight: theme.spacing(6),
  },
  messagePreview: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    color: theme.palette.text.secondary,
  },
  shortcode: {
    fontWeight: 600,
    color: theme.palette.primary.main,
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(4),
  },
  iconWrapper: {
    marginBottom: theme.spacing(2),
  },
  viewButton: {
    padding: theme.spacing(0.5),
    color: theme.palette.primary.main,
    "&:hover": {
      backgroundColor: theme.palette.primary.light + "20",
    },
  },
  previewContent: {
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    minHeight: "100px",
    maxHeight: "300px",
    overflowY: "auto",
  },
  previewTitle: {
    color: theme.palette.primary.main,
    fontWeight: 600,
  },
  tooltip: {
    maxWidth: 350,
    fontSize: "0.85rem",
    whiteSpace: "pre-wrap",
  },
  previewImage: {
    maxWidth: "100%",
    maxHeight: "400px",
    objectFit: "contain",
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  mediaIndicator: {
    display: "inline-flex",
    alignItems: "center",
    marginLeft: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: "0.75rem",
  },
  mediaIcon: {
    fontSize: "1rem",
    marginRight: theme.spacing(0.5),
  },
}));

const QuickMessageModal = ({ open, onClose, onSelectMessage }) => {
  const classes = useStyles();
  const [quickMessages, setQuickMessages] = useState([]);
  const [filteredMessages, setFilteredMessages] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewMessage, setPreviewMessage] = useState(null);

  useEffect(() => {
    if (open) {
      fetchQuickMessages();
    }
  }, [open]);

  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredMessages(quickMessages);
    } else {
      const filtered = quickMessages.filter(
        (msg) =>
          msg.shortcode.toLowerCase().includes(searchTerm.toLowerCase()) ||
          msg.message.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredMessages(filtered);
    }
  }, [searchTerm, quickMessages]);

  const fetchQuickMessages = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/quick-messages/company");
      setQuickMessages(data);
      setFilteredMessages(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectMessage = (message) => {
    onSelectMessage({
      value: message.message,
      // mediaPath continua no payload por compatibilidade; medias é a fonte atual.
      mediaPath: message.mediaPath,
      medias: getQuickMessageMedias(message),
      shortcode: message.shortcode,
      documentTemplateId: message.documentTemplateId,
    });
    handleClose();
  };

  const handleClose = () => {
    setSearchTerm("");
    onClose();
  };

  const handleOpenPreview = (e, message) => {
    e.stopPropagation();
    setPreviewMessage(message);
    setPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewMessage(null);
  };

  const truncateMessage = (text, maxLength = 60) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={handleClose}
      disableRestoreFocus
      className={classes.dialog}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle className={classes.header}>
        <Typography variant="h6">
          {i18n.t("quickMessages.modal.title")}
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <TextField
          className={classes.searchField}
          fullWidth
          placeholder={i18n.t("quickMessages.modal.search")}
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
        <Divider />
        {loading ? (
          <Box className={classes.loadingContainer}>
            <CircularProgress />
          </Box>
        ) : filteredMessages.length === 0 ? (
          <Box className={classes.emptyState}>
            <Box className={classes.iconWrapper}>
              <FlashOnIcon style={{ fontSize: 48 }} />
            </Box>
            <Typography variant="body1">
              {searchTerm
                ? i18n.t("quickMessages.modal.noResults")
                : i18n.t("quickMessages.modal.empty")}
            </Typography>
          </Box>
        ) : (
          <List>
            {filteredMessages.map((message) => {
              const medias = getQuickMessageMedias(message);
              return (
              <React.Fragment key={message.id}>
                <Tooltip 
                  title={message.message} 
                  placement="left"
                  classes={{ tooltip: classes.tooltip }}
                  arrow
                >
                  <ListItem
                    className={classes.listItem}
                    onClick={() => handleSelectMessage(message)}
                  >
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center">
                          <Typography className={classes.shortcode}>
                            /{message.shortcode}
                          </Typography>
                          {medias.length > 0 && (
                            <span className={classes.mediaIndicator}>
                              <AttachFileIcon className={classes.mediaIcon} />
                              {medias.length > 1
                                ? `${medias.length} ${i18n.t("quickMessages.table.filesSuffix")}`
                                : i18n.t("quickMessages.modal.hasMedia")}
                            </span>
                          )}
                        </Box>
                      }
                      secondary={
                        <Typography
                          variant="body2"
                          className={classes.messagePreview}
                        >
                          {truncateMessage(message.message)}
                        </Typography>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Tooltip title={i18n.t("quickMessages.modal.viewMessage")} arrow>
                        <IconButton
                          className={classes.viewButton}
                          onClick={(e) => handleOpenPreview(e, message)}
                          size="small"
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                </Tooltip>
              </React.Fragment>
              );
            })}
          </List>
        )}
      </DialogContent>
    </Dialog>

    {/* Modal de Visualização da Mensagem */}
    <Dialog
      open={previewOpen}
      onClose={handleClosePreview}
      className={classes.previewDialog}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle className={classes.header}>
        <Typography variant="h6">
          {previewMessage && (
            <span className={classes.previewTitle}>
              /{previewMessage.shortcode}
            </span>
          )}
        </Typography>
        <IconButton onClick={handleClosePreview} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {/* Grade com todos os anexos. O onError esconde os arquivos que não
            existem mais no servidor ou que não são imagens. */}
        {getQuickMessageMedias(previewMessage).map((media, index) => (
          <Box
            key={media.id ?? `preview-${index}`}
            display="flex"
            flexDirection="column"
            alignItems="center"
            mb={2}
          >
            <img
              src={media.mediaPath}
              alt={media.mediaName || i18n.t("quickMessages.dialog.mediaAlt")}
              className={classes.previewImage}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            {media.mediaName && (
              <Typography variant="caption" color="textSecondary">
                {media.mediaName}
              </Typography>
            )}
          </Box>
        ))}
        <Box className={classes.previewContent}>
          <Typography variant="body1" style={{ whiteSpace: "pre-wrap" }}>
            {previewMessage?.message}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClosePreview} color="default">
          {i18n.t("quickMessages.modal.close")}
        </Button>
        <Button 
          onClick={() => {
            handleSelectMessage(previewMessage);
            handleClosePreview();
          }} 
          color="primary"
          variant="contained"
        >
          {i18n.t("quickMessages.modal.useMessage")}
        </Button>
      </DialogActions>
    </Dialog>
  </>
  );
};

export default QuickMessageModal;
